/* eslint-disable complexity */
import { Alert, Badge, FadeIn } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, getCalendarMonthRange } from "common";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseScheduleType, EquipmentResource, Event, Permission, Timeslot } from "types";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";
import { DatesSetArg, EventApi, EventInput } from "@fullcalendar/common";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import { LocalizedFullCalendar } from "../../Common/LocalizedFullCalendar/LocalizedFullCalender";
import { NoTranslate } from "../../Common/NoTranslate";
import { DashboardRosterDrawer } from "../../PurchasesMenu/DashboardRosterDrawer";
import { getTimeslotParams } from "../Common/timeslotParams";
import { DashboardDayItem } from "../DashboardDayItem";
import { DashboardSummaryHeader } from "../DashboardSummaryHeader";
import { useDashboardTableStore } from "../DashboardTable/useDashboardTableStore";
import { DashboardCalendarItemContent } from "./DashboardCalendarItemContent";
import { DashboardCalendarRestrictedItemContent } from "./DashboardCalendarRestrictedItemContent";
import { DashboardEquipmentPagination as dashboardEquipmentPagination } from "./DashboardEquipmentPagination";
import { DayHeaderContent } from "./DayHeaderContent";
import { SelectedEventsToolbar } from "./SelectedEventsToolbar";
import { EventObject, EventType, useCalendarEvents } from "./useCalendarEvents";
// Needs to go first, solves a problem with Vite.
import { config } from "@/config";
import { useScrollToTimeslotCalendar } from "@/hooks/Dashboard/useScrollToTimeslot";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCaptureQueryError } from "@/hooks/useCaptureQueryError";
import { useCollection } from "@/hooks/useCollection";
import { useWeeklyTimeslotCollections } from "@/hooks/useWeeklyTimeslots";
import { getTimeslots } from "@/models/BaseSchedule";
import { canOpenRoster, getId } from "@/models/Timeslot";
import { DateNavigationDirection, useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";
import { DashboardMode, DashboardScope } from "@/types/Dashboard";

interface DayCellContentProps {
    isToday: boolean;
    dayNumberText: string;
}

interface DashboardCalendarProps {
    height: number;
}

export const DashboardCalendar = ({ height }: DashboardCalendarProps) => {
    const {
        date: currentDate,
        filters,
        setDate,
        isRealTimeUpdate,
    } = useDashboardFilterStore((state) => state, shallow);
    const [isDrawerOpen, drawerTimeslotId, storedTimeslot, setDrawerClose, setTimeslot] = useRosterDrawerStore(
        (state) => [state.isOpen, state.timeslotId, state.timeslot, state.setClose, state.setTimeslot],
        shallow,
    );

    const dashboardMode = filters.mode;
    const isDayMode = dashboardMode === DashboardMode.DAY;
    const isWeekMode = dashboardMode === DashboardMode.WEEK;
    const isEquipmentMode = dashboardMode === DashboardMode.EQUIPMENT;
    const isMonthMode = dashboardMode === DashboardMode.MONTH;
    const isMonthAllView = filters.scope === DashboardScope.ALL && isMonthMode;

    const { isGranted } = useAuthorization();

    const range = getRange(dashboardMode);
    const [startParam, setStartParam] = useState(calculateFirstDateForCalendar(currentDate, dashboardMode));

    const [debouncedStartParam] = useDebounce(startParam, isMonthMode ? 500 : 200); // Bigger debounce for month view because it's a lot of data
    const endParam = range ? debouncedStartParam.add(range, "days") : null;
    const limit = 500;

    // The month grid can store up to 42 cells, for example if the 1 day of the month starts on Sunday
    // The calendar will display 6 days of the previous month and at least 5 of the next one.
    const getParams = getTimeslotParams();
    const params = useMemo(() => {
        return getParams(debouncedStartParam, endParam, { limit });
    }, [getParams, debouncedStartParam, endParam, limit]);

    const [selectedEvents, setSelectedEvents] = useState<Set<Event>>(new Set<Event>());
    const [selectedTimeslots, setSelectedTimeslots] = useState<Set<Timeslot>>(new Set<Timeslot>());
    const [selectedTimeslotIds, setSelectedTimeslotIds] = useState<Set<string>>(new Set<string>());
    const updateEventSource = useCallback(
        (events: EventInput[], sourceId: string) => {
            const calendarApi = calendarRef.current?.getApi();
            if (!calendarApi || isMonthMode) return;

            const existingSource = calendarApi
                .getEventSources()
                .find((source) => source.internalEventSource.sourceId === sourceId);

            if (existingSource) {
                existingSource.remove();
            }

            calendarApi.addEventSource({
                id: sourceId,
                events,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMonthMode],
    );

    const weeklyResponses = useWeeklyTimeslotCollections(startParam, endParam!, isMonthMode, filters.scope);
    const isLoadingData = weeklyResponses.some((response) => {
        return response.isLoading;
    });
    const isLoadedFirstWeek = weeklyResponses[0].data.length > 0 || weeklyResponses[0].isFetched;
    const isTimeslotsError = weeklyResponses.some((weeklyResponse) => weeklyResponse.isError);
    const isQueryAbortedDueToCacheLimit = weeklyResponses.some(
        (weeklyResponse) => weeklyResponse.isQueryAbortedDueToCacheLimit,
    );
    const timeslotsError = weeklyResponses.find((weeklyResponse) => weeklyResponse.error)?.error;

    // Track actual data content changes to avoid unnecessary re-renders in month view.
    // Uses a fingerprint derived from item counts and IDs rather than dataUpdatedAt timestamps,
    // since timestamps change on every React Query refetch even when data is identical.
    const dataContentRef = useRef<string>("");
    const dataContentFingerprint = useMemo(() => {
        const fingerprint = weeklyResponses
            .map((r) => {
                const data = r.data;
                if (!data?.length) return "0";
                const first = data[0] as Timeslot;
                const last = data[data.length - 1] as Timeslot;
                // Include reservation counts so cancellations that change capacity trigger updates
                const reserved = data.reduce((sum, t: any) => sum + (t.capacity?.reserved ?? 0), 0);
                return `${data.length}:${getId(first)}:${getId(last)}:${reserved}`;
            })
            .join(",");
        return `${filters.scope}:${fingerprint}`;
    }, [weeklyResponses, filters.scope]);

    // We will always have weeks length is equal to 6
    // and there will be no error with a different number of hooks when rerendering the component.
    const events = weeklyResponses.map((response) =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCalendarEvents({
            timeslots: response.data as Timeslot[],
            dashboardMode,
            selectedTimeslotIds,
            selectedEvents,
            currentDate,
            isTimeslotError: response.isError,
        }),
    );

    // Only replace the full event source when actual timeslot data changes (not on selection changes).
    // Full source replacement causes FullCalendar to re-render all events, resetting scroll position.
    // Month mode uses the `events` prop with a stabilized reference instead — see allTimeslots memo.
    // Week/Day modes use addEventSource on every events change (no scroll-to-top issue in timeGrid views).
    useEffect(() => {
        if (isEquipmentMode || isMonthMode) return;

        const mergedEvents = events.flat().filter(Boolean);
        updateEventSource(mergedEvents, "month-events");
    }, [events, updateEventSource, isEquipmentMode, isMonthMode]);

    // Handle selection class updates in-place via FullCalendar API to avoid scroll reset.
    // When user Ctrl+clicks, we update individual event classNames without replacing the source.
    useEffect(() => {
        if (isEquipmentMode) return;

        const calendarApi = calendarRef.current?.getApi();
        if (!calendarApi) return;

        const selectedEventIdSet = new Set([...selectedEvents].map((e) => e.id));
        const calendarEvents = calendarApi.getEvents();
        for (const fcEvent of calendarEvents) {
            const timeslot = fcEvent.extendedProps.timeslot;
            const event = fcEvent.extendedProps.event;
            if (!timeslot) continue;

            const isSelected = selectedTimeslotIds.has(timeslot.id) || (event && selectedEventIdSet.has(event.id));

            const currentClasses = [...fcEvent.classNames];
            const hasSelectedClass = currentClasses.includes("selected");

            if (isSelected && !hasSelectedClass) {
                fcEvent.setProp("classNames", [...currentClasses, "selected"]);
            } else if (!isSelected && hasSelectedClass) {
                fcEvent.setProp(
                    "classNames",
                    currentClasses.filter((c) => c !== "selected"),
                );
            }
        }
    }, [selectedTimeslotIds, selectedEvents, isEquipmentMode]);

    useCaptureQueryError(timeslotsError, {
        tags: { component: "DashboardCalendar", action: "fetchTimeslots" },
        extra: { params },
    });

    const allWeeklyTimeslots = weeklyResponses.flatMap((weekResponse) => weekResponse.data ?? []);
    const { calendarRef } = useScrollToTimeslotCalendar(debouncedStartParam, allWeeklyTimeslots, isRealTimeUpdate);

    const drawerTimeslot = allWeeklyTimeslots.find(
        (timeslot) => timeslot && getId(timeslot as Timeslot) === drawerTimeslotId,
    );

    // If drawer is open but timeslot not found in filtered results, use stored version from store
    const activeDrawerTimeslot = drawerTimeslot ?? (isDrawerOpen ? storedTimeslot : null);

    const { data: resources = [], isLoading: isLoadingResources } = useCollection<EquipmentResource>(
        "resources",
        null,
        {
            enabled: isGranted(Permission.VIEW_RESOURCES_EQUIPMENT) && isEquipmentMode,
        },
    );

    // show only filtered equipments
    const filteredResources =
        !filters || filters.resource.all
            ? resources
            : resources.filter((resource) => filters.resource.selected.includes(resource.id));

    const calendarResources = isDayMode
        ? [{ id: formatDate(currentDate, DateFormat.DATE_ISO) }]
        : filteredResources.map((resource) => {
              return {
                  id: resource.id,
                  title: resource.count > 1 ? `${resource.name} (${resource.count})` : resource.name,
              };
          });

    // Sync dashboard date change to the calendar view.
    useEffect(() => {
        calendarRef.current?.getApi().gotoDate(currentDate?.toDate());
    }, [currentDate, calendarRef]);

    useEffect(() => {
        if (isWeekMode) {
            calendarRef.current?.getApi().changeView("timeGridWeek");
        }

        if (isMonthMode) {
            calendarRef.current?.getApi().changeView("dayGridMonth");
        }

        if (isEquipmentMode || isDayMode) {
            calendarRef.current?.getApi().changeView("resourceTimeGridDay");
        }

        clearSelected();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardMode, isDayMode]);

    const restrictedResourceEvents = useMemo(() => {
        return debouncedStartParam && isEquipmentMode
            ? getRestrictedResourceEvents(debouncedStartParam, resources)
            : [];
    }, [debouncedStartParam, resources, isEquipmentMode]);

    const allTimeslotsRef = useRef<any>([]);

    const allTimeslots = useMemo(() => {
        const calendarEvents = isMonthAllView ? events[0] : events.flat();
        if (calendarEvents === undefined) {
            // Return the previous value of allTimeslots
            return allTimeslotsRef.current;
        }

        // For month mode, only update when actual data changes (not on selection changes)
        // to prevent FullCalendar from re-rendering and resetting scroll position
        if (isMonthMode && dataContentFingerprint === dataContentRef.current && allTimeslotsRef.current.length > 0) {
            return allTimeslotsRef.current;
        }

        if (isMonthMode) {
            dataContentRef.current = dataContentFingerprint;
        }

        // Calculate the new value of allTimeslots
        const updatedTimeslots =
            debouncedStartParam && isEquipmentMode ? [...calendarEvents, ...restrictedResourceEvents] : calendarEvents;
        // Update the reference with the new value
        allTimeslotsRef.current = updatedTimeslots;

        // Return the new value of allTimeslots
        return updatedTimeslots;
    }, [
        isMonthMode,
        isMonthAllView,
        events,
        restrictedResourceEvents,
        debouncedStartParam,
        isEquipmentMode,
        dataContentFingerprint,
    ]);

    const {
        // This might look weird, but exposing that field and the reset function is much more efficient than using
        // several callbacks and state/setState bloat -Alexander
        resetEquipmentPage,
        visibleResources,
        jsx: DashboardEquipmentPagination,
    } = dashboardEquipmentPagination({
        calendarResources,
        scope: filters.scope,
        allTimeslots,
    });

    const isMultiSelected = selectedEvents.size > 0 || selectedTimeslots.size > 0;

    const clearSelected = useCallback(() => {
        setSelectedEvents(new Set());
        setSelectedTimeslots(new Set());
        setSelectedTimeslotIds(new Set());
    }, []);

    useEffect(() => {
        let clearSelectedOnEscapeHandler: any = null;

        if (isMultiSelected) {
            clearSelectedOnEscapeHandler = (event: any) => {
                if (event.key === "Escape") {
                    clearSelected();
                }
            };

            document.addEventListener("keydown", clearSelectedOnEscapeHandler, false);
        }

        return () => {
            if (clearSelectedOnEscapeHandler) {
                document.removeEventListener("keydown", clearSelectedOnEscapeHandler, false);
            }
        };
    }, [isMultiSelected, clearSelected]);

    const handleCustomButtonClick = (direction: DateNavigationDirection) => {
        switch (direction) {
            case "prev": {
                calendarRef.current?.getApi()?.prev();
                setDate(dayjs(calendarRef.current?.getApi()?.view?.currentStart));
                break;
            }

            case "next": {
                calendarRef.current?.getApi()?.next();
                setDate(dayjs(calendarRef.current?.getApi()?.view?.currentStart));
                break;
            }

            case "today": {
                calendarRef.current?.getApi()?.today();
                setDate(dayjs());
                break;
            }

            default:
                break;
        }

        resetEquipmentPage();
    };

    const handleDateChange = (date: Date) => {
        setDate(dayjs(date));
    };

    const handleDatesSet = (range: DatesSetArg) => {
        setStartParam(dayjs(range.start));
    };

    const toggleEventSelection = (fcEvent: EventApi) => {
        const event = fcEvent.extendedProps.event;
        const timeslot = fcEvent.extendedProps.timeslot;
        if (!canOpenRoster(timeslot)) {
            return;
        }

        const timeslotId = getId(timeslot);

        if (event) {
            // Check if the event is already selected by its unique ID
            // Using object reference directly won't work after FullCalendar re-renders events
            if ([...selectedEvents].some((e) => e.id === event.id)) {
                // If selected, remove the event from the set
                setSelectedEvents(new Set([...selectedEvents].filter((e) => e.id !== event.id)));
            } else {
                // If not selected, add the full event object to the set
                setSelectedEvents(new Set([...selectedEvents, event]));
            }
        } else if (selectedTimeslotIds.has(timeslotId)) {
            // If a timeslot (without an event) is already selected, remove it
            const timeslots = [...selectedTimeslots].filter((t) => getId(t) !== timeslotId);
            setSelectedTimeslots(new Set(timeslots));
            setSelectedTimeslotIds(new Set([...selectedTimeslotIds].filter((id) => id !== timeslotId)));
        } else {
            // If timeslot is not selected, add it along with its ID
            setSelectedTimeslots(new Set([...selectedTimeslots, timeslot]));
            setSelectedTimeslotIds(new Set([...selectedTimeslotIds, timeslotId]));
        }
    };

    const initialView: string = isWeekMode ? "timeGridWeek" : isMonthMode ? "dayGridMonth" : "resourceTimeGridDay";
    const viewName = initialView.toLocaleLowerCase();

    const dayNumberOfEvents = Math.floor(window.innerWidth / 300); // 300px approx size of one event plate in day row

    const isFetchingNextPage = weeklyResponses.some((response) => {
        return response.isFetchingNextPage;
    });

    const isFetching = weeklyResponses.some((response) => {
        return response.isFetching;
    });
    const isFetchingOrHasNextPages = isLoadingData || isFetchingNextPage || isFetching;

    const isLoadingTimeslots = isFetchingOrHasNextPages || !currentDate || (isEquipmentMode && isLoadingResources);

    useEffect(() => {
        useDashboardTableStore.setState({ isLoading: isLoadingTimeslots });
    }, [isLoadingTimeslots]);

    const handleDrawerClose = () => {
        if (!drawerTimeslotId) {
            setTimeslot(null);
            setDrawerClose();
            return;
        }

        const safeId = normalizeTimeslotId(drawerTimeslotId);
        const safeSelector = `#${safeId}`;

        document.body.style.cursor = "wait";

        // The original value of 100s is too long for timeslots to appear in
        // dashboard calendar, so reducing it to 3s otherwise incase the timeslot
        // doesn't exist after we make any changes. it wll prevent closing the drawer.
        waitForElement(safeSelector, 3000)
            .then((element) => {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            })
            .catch((error) => {
                console.warn(error.message);
            })
            .finally(() => {
                document.body.style.cursor = "";
                setTimeslot(null);
                setDrawerClose();
            });
    };

    // Ensure the summary contains only the selected month's data.
    const summaryTimeslots = useMemo(() => {
        if (!isMonthMode) return allWeeklyTimeslots as Timeslot[];

        const monthStart = currentDate.startOf("month");
        const monthEnd = currentDate.endOf("month");

        return (allWeeklyTimeslots as Timeslot[]).filter((timeslot) => {
            const tsDate = dayjs(timeslot.date);
            return tsDate.isSameOrAfter(monthStart, "day") && tsDate.isSameOrBefore(monthEnd, "day");
        });
    }, [allWeeklyTimeslots, currentDate, isMonthMode]);

    return (
        <>
            <DashboardSummaryHeader
                date={currentDate.format()}
                timeslots={summaryTimeslots}
                isCollapsed={false}
                handleCustomButtonClick={handleCustomButtonClick}
                onChange={handleDateChange}
            />
            <FadeIn>
                <div
                    style={{ height }}
                    className={clsx(
                        isDayMode ? `${viewName}-specific-day` : `${viewName}-specific`,
                        isEquipmentMode ? "relative" : "",
                    )}
                >
                    {allTimeslots.length === 0 && isTimeslotsError && (
                        <Alert className="mb-5 text-center" color="danger">
                            An unexpected error occurred. No timeslots found.
                        </Alert>
                    )}

                    {/* DashboardEquipmentPagination is a proper JSX element, but imported along with exposed fields of that component, that must be used directly by the calendar */}
                    {isEquipmentMode && DashboardEquipmentPagination}

                    {isQueryAbortedDueToCacheLimit && (
                        <Alert className="mb-5 text-center" color="warning">
                            <div>
                                Not all events for the selected month are currently displayed due to the calendar event
                                limit.
                            </div>
                            <div>
                                Please adjust the filters for product, guide, or equipment to narrow down the results
                                and view more relevant events.
                            </div>
                        </Alert>
                    )}

                    <LocalizedFullCalendar
                        ref={calendarRef}
                        nowIndicator
                        events={isEquipmentMode || isMonthMode ? allTimeslots : []}
                        eventSources={undefined}
                        schedulerLicenseKey={config.fullCalendar?.licenseKey}
                        height="100%"
                        initialView={initialView}
                        plugins={[timeGridPlugin, resourceTimeGridPlugin, dayGridPlugin, interactionPlugin]}
                        headerToolbar={false}
                        dayCellClassNames={["h-24"]}
                        eventMinHeight={40}
                        initialDate={currentDate.toDate()}
                        datesSet={handleDatesSet}
                        eventContent={(props) => {
                            if (!isLoadedFirstWeek) {
                                return null;
                            }

                            if (props.event.extendedProps.type === EventType.TIMESLOT) {
                                if (isDayMode) {
                                    const { event } = props.event.extendedProps;
                                    return (
                                        <DashboardDayItem
                                            {...props}
                                            isSelected={event && selectedEvents.has(event)}
                                            toggleEventSelection={toggleEventSelection}
                                        />
                                    );
                                }

                                return (
                                    <DashboardCalendarItemContent
                                        event={props.event}
                                        toggleEventSelection={toggleEventSelection}
                                    />
                                );
                            }

                            if (props.event.extendedProps.type === EventType.RESTRICTED) {
                                const { event } = props;

                                return (
                                    <DashboardCalendarRestrictedItemContent
                                        title={event.title}
                                        start={event.start!}
                                        end={event.end!}
                                        allDay={event.allDay}
                                    />
                                );
                            }

                            return null;
                        }}
                        eventClassNames={["overflow-hidden font-sans !bg-transparent !border-none !shadow-none"]}
                        scrollTime={dayjs().subtract(90, "minutes").format(DateFormat.TIME_24H)}
                        eventOrder="start"
                        // Slot customization
                        // slotMinTime="10:00" // Debugging
                        slotDuration="00:30:00"
                        // slotLabelInterval="00:15:00"
                        slotLabelContent={SlotLabelContent}
                        dayHeaderContent={
                            isMonthMode
                                ? getDayHeaderContentMonth // Will display Day row titles as "Wednesday" instead of "Wed"
                                : (props) => <DayHeaderContent fcEvents={allTimeslots} {...props} />
                        }
                        allDayText="All Day"
                        allDaySlot={showAllDayEvent(allTimeslots)}
                        dayMaxEvents={
                            isMonthMode
                                ? filters.scope === DashboardScope.ALL && isQueryAbortedDueToCacheLimit
                                    ? 75
                                    : allTimeslots.length
                                : 2
                        }
                        dayCellContent={(props) =>
                            !isDayMode && <DayCellContent isToday={props.isToday} dayNumberText={props.dayNumberText} />
                        }
                        // All day
                        moreLinkClick={() => {
                            // Quick fix because the lib doesn't support outside click control, it does forcefully
                            // @link https://github.com/fullcalendar/fullcalendar/blob/master/packages/common/src/common/Popover.tsx#L65
                            document.addEventListener("mousedown", (event) => {
                                event.stopImmediatePropagation();
                            });
                            return "popover";
                        }}
                        resources={isEquipmentMode ? visibleResources : calendarResources}
                        resourceOrder={isEquipmentMode ? "title" : undefined}
                        views={{
                            resourceTimeGridDay: {
                                resourceLabelClassNames: "break-all !h-9 !pt-1.5 text-gray-darker",
                                filterResourcesWithEvents: filters.scope === DashboardScope.ASSIGNED,
                            },
                        }}
                        moreLinkClassNames="w-8 pt-1 pl-1"
                        // contentHeight="auto" // Removed 'contentHeight' prop as it was causing compatibility issues with 'scrollTime'; removal ensures proper functionality.
                        slotEventOverlap={false} // So we can show multiple events in a shorter column
                        // Day header
                        dayHeaderClassNames={clsx("group relative", { "no-translate": isMonthMode })}
                        allDayClassNames="font-semibold"
                        // Max of $X events per slot and clicking on "more" takes you to day view
                        eventMaxStack={isDayMode ? dayNumberOfEvents : 2}
                    />
                </div>
            </FadeIn>
            {selectedEvents.size > 0 || selectedTimeslots.size > 0 ? (
                <SelectedEventsToolbar
                    events={[...selectedEvents]}
                    timeslots={[...selectedTimeslots]}
                    calendarRef={calendarRef}
                    onClearSelected={clearSelected}
                />
            ) : null}

            <DashboardRosterDrawer
                isOpen={isDrawerOpen}
                timeslot={activeDrawerTimeslot as Timeslot}
                onClose={handleDrawerClose}
            />
        </>
    );
};

export const normalizeTimeslotId = (id: string): string => {
    const encoded = base64Encode(id);
    return `ts-${encoded.replace(/[^a-z\d]/gi, "")}`;
};

const waitForElement = async (selector: string, timeout = 100_000): Promise<HTMLElement> => {
    return new Promise((resolve, reject) => {
        const interval = 50;
        let elapsed = 0;

        const check = () => {
            const element = document.querySelector<HTMLElement>(selector);
            if (element) return resolve(element);

            elapsed += interval;
            if (elapsed >= timeout) return reject(new Error("Element not found in time"));

            requestAnimationFrame(check);
        };

        check();
    });
};

const getRange = (mode: DashboardMode) => {
    switch (mode) {
        case DashboardMode.LIST:
            return 2;
        case DashboardMode.DAY:
            return 1;
        case DashboardMode.EQUIPMENT:
            return 1;
        case DashboardMode.WEEK:
            return 6;
        case DashboardMode.MONTH:
            // We get 6 visible weeks of the fullcalendar.
            return 42;
        case DashboardMode.TABLE:
            return 1;
        default:
            return 0;
    }
};

const getRestrictedResourceEvents = (date: Dayjs, resources: EquipmentResource[]) => {
    const restrictedEvents = [];

    for (const resource of resources) {
        const unavailableSchedules = resource.schedules?.filter((schedule) => {
            return schedule.type === BaseScheduleType.UNAVAILABLE;
        });

        for (const schedule of unavailableSchedules ?? []) {
            const timeslots = getTimeslots(schedule, date);

            for (const timeslot of timeslots) {
                restrictedEvents.push({
                    resourceId: resource.id,
                    title: timeslot.title ?? "",
                    start: timeslot.start.toDate(),
                    end: timeslot.end.toDate(),
                    // Extended props
                    type: EventType.RESTRICTED,
                    allDay: timeslot.allDay,
                });
            }
        }
    }

    return restrictedEvents;
};

const calculateFirstDateForCalendar = (date: Dayjs, mode: DashboardMode) => {
    if (mode === DashboardMode.MONTH) {
        const { start } = getCalendarMonthRange(date);
        return start;
    }

    return date;
};

const SlotLabelContent = ({ text }: { text: string }) => {
    const isMinor = text.includes(":");
    const hour = `${text.slice(0, -2)} ${text.slice(-2)}`; // 10PM to "10 PM"
    const content = isMinor ? text.slice(text.indexOf(":") + 1, -2) : hour;
    return (
        <NoTranslate
            as="span"
            className={clsx("slot-label-content", isMinor ? "text-gray-darker" : "font-bold uppercase")}
        >
            {content}
        </NoTranslate>
    );
};

// Day of the week in month view. Will display Day row titles as "Wednesday" instead of "Wed"
const getDayHeaderContentMonth = ({ date }: { date: string }) => dayjs.tz(date, "UTC").format("dddd");

const DayCellContent = ({ isToday, dayNumberText }: DayCellContentProps) => {
    return (
        <div className="flex justify-between">
            {isToday && <Badge color="primary">Today</Badge>}
            <NoTranslate as="span">{dayNumberText}</NoTranslate>
        </div>
    );
};

const showAllDayEvent = (timeslot: EventObject[]) => {
    return timeslot.some((timeslot) => !!timeslot.allDay);
};

export const base64Encode = (string: string): string => {
    const bytes = new TextEncoder().encode(string);

    return btoa(String.fromCodePoint(...bytes));
};
